import { useState, useEffect } from 'react';
import { BarChart2, Shield, RefreshCw } from 'lucide-react';
import { getAgentProduction } from '../api.js';

// Paso 3 — owner-only "Producción por agente" report. Numbers only across the
// 7 categories (cartera, producción, comisiones, conversión, renovaciones,
// retención, actividad). Never shows clients or PHI — the backend returns
// aggregates only. Critical for private-books agencies where the owner cannot
// see agents' clients but still needs to oversee production.
export function ProductionByAgentView({ lang, currentUser }) {
  const es = lang === 'es';
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const load = async () => {
    setLoading(true);
    setError('');
    try {
      setData(await getAgentProduction());
    } catch (err) {
      setError(err.message || (es ? 'No se pudo cargar el reporte' : 'Failed to load report'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const money = (n) =>
    new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(n || 0);
  // Dates always en-US (mm/dd/yyyy) regardless of UI language.
  const fmtDate = (iso) => (iso ? new Date(iso).toLocaleDateString('en-US') : (es ? 'Nunca' : 'Never'));
  const roleLabel = (r) =>
    r === 'AGENCY_OWNER' ? (es ? 'Dueño' : 'Owner')
    : r === 'SOLO_AGENT' ? (es ? 'Agente solo' : 'Solo agent')
    : (es ? 'Agente' : 'Agent');

  // Column definitions — key, header, kind ('num' | 'money' | 'name' | 'date'),
  // and which category group it belongs to (for the colored group header).
  const COLS = [
    { key: 'name', es: 'Agente', en: 'Agent', kind: 'name', grp: '' },
    { key: 'clientsActive', es: 'Clientes activos', en: 'Active clients', kind: 'num', grp: 'cartera' },
    { key: 'clientsNew', es: 'Clientes nuevos (mes)', en: 'New clients (mo.)', kind: 'num', grp: 'cartera' },
    { key: 'policiesActive', es: 'Pólizas activas', en: 'Active policies', kind: 'num', grp: 'prod' },
    { key: 'policiesNew', es: 'Pólizas nuevas (mes)', en: 'New policies (mo.)', kind: 'num', grp: 'prod' },
    { key: 'annualPremium', es: 'Prima anual', en: 'Annual premium', kind: 'money', grp: 'prod' },
    { key: 'commissionMonth', es: 'Comisión (mes)', en: 'Commission (mo.)', kind: 'money', grp: 'com' },
    { key: 'commissionYear', es: 'Comisión (año)', en: 'Commission (yr.)', kind: 'money', grp: 'com' },
    { key: 'leadsMonth', es: 'Leads (mes)', en: 'Leads (mo.)', kind: 'num', grp: 'conv' },
    { key: 'convertedMonth', es: 'Convertidos (mes)', en: 'Converted (mo.)', kind: 'num', grp: 'conv' },
    { key: 'renewals30', es: 'Renov. 30d', en: 'Renewals 30d', kind: 'num', grp: 'ren' },
    { key: 'churn', es: 'Bajas', en: 'Churn', kind: 'num', grp: 'ret' },
    { key: 'lastActivityAt', es: 'Última actividad', en: 'Last activity', kind: 'date', grp: '' },
  ];

  const th = { padding: '.55rem .65rem', fontWeight: 700, fontSize: '.68rem', color: 'var(--c-text3,#45688a)', textTransform: 'uppercase', letterSpacing: '.3px', whiteSpace: 'nowrap' };
  const td = { padding: '.5rem .65rem', fontSize: '.8rem', color: 'var(--c-white,#eef6ff)', whiteSpace: 'nowrap' };

  const cell = (col, r, isTotal) => {
    const v = r[col.key];
    if (col.kind === 'name') {
      return (
        <td key={col.key} style={{ ...td, fontWeight: isTotal ? 800 : 600 }}>
          {isTotal ? (es ? `TOTAL · ${data.rows.length}` : `TOTAL · ${data.rows.length}`) : (
            <>
              {r.name}
              <span style={{ fontSize: '.6rem', color: 'var(--c-text3,#45688a)', marginLeft: 6 }}>{roleLabel(r.role)}</span>
            </>
          )}
        </td>
      );
    }
    if (col.kind === 'date') return <td key={col.key} style={{ ...td, color: 'var(--c-text2,#7aa3c0)' }}>{isTotal ? '—' : fmtDate(v)}</td>;
    const display = col.kind === 'money' ? money(v) : v;
    return <td key={col.key} style={{ ...td, textAlign: 'right', fontWeight: isTotal ? 800 : 400 }}>{display}</td>;
  };

  return (
    <div>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1rem', flexWrap: 'wrap', gap: '.5rem' }}>
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '.5rem' }}>
            <BarChart2 size={20} style={{ color: 'var(--c-cyan,#00d4ff)' }} />
            <h2 style={{ fontWeight: 800, fontSize: '1.1rem', color: 'var(--c-white,#eef6ff)', margin: 0 }}>
              {es ? 'Producción por Agente' : 'Production by Agent'}
            </h2>
          </div>
          <p style={{ fontSize: '.72rem', color: 'var(--c-text3,#45688a)', margin: '.25rem 0 0' }}>
            {es ? 'Números de cada agente — sin acceso a sus clientes' : "Each agent's numbers — no access to their clients"}
          </p>
        </div>
        <button type="button" onClick={load} disabled={loading}
          style={{ display: 'flex', alignItems: 'center', gap: 5, background: 'var(--c-surface2,#112538)', border: '1px solid var(--c-border,#1a3550)', color: 'var(--c-cyan,#00d4ff)', fontSize: '.75rem', fontWeight: 700, padding: '.45rem .85rem', borderRadius: 9, cursor: loading ? 'default' : 'pointer', opacity: loading ? 0.6 : 1, fontFamily: 'inherit' }}>
          <RefreshCw size={14} /> {es ? 'Actualizar' : 'Refresh'}
        </button>
      </div>

      {/* Privacy note */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '.45rem', background: 'rgba(0,212,255,.07)', border: '1px solid rgba(0,212,255,.3)', borderRadius: 10, padding: '.5rem .75rem', marginBottom: '1rem' }}>
        <Shield size={14} style={{ color: 'var(--c-cyan,#00d4ff)', flexShrink: 0 }} />
        <span style={{ fontSize: '.7rem', color: 'var(--c-text2,#7aa3c0)' }}>
          {es ? 'Solo totales agregados. Esta vista nunca expone clientes ni su información. Desliza → para ver todas las columnas.'
              : 'Aggregated totals only. This view never exposes clients or their information. Scroll → to see all columns.'}
        </span>
      </div>

      {loading && (
        <div style={{ textAlign: 'center', padding: '2rem', color: 'var(--c-text3,#45688a)', fontSize: '.85rem' }}>
          {es ? 'Cargando…' : 'Loading…'}
        </div>
      )}

      {error && !loading && (
        <div style={{ background: 'rgba(239,68,68,.1)', border: '1px solid rgba(239,68,68,.35)', borderRadius: 10, padding: '.75rem 1rem', color: '#fca5a5', fontSize: '.8rem' }}>
          {error}
        </div>
      )}

      {!loading && !error && data && (
        data.rows.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '2rem', color: 'var(--c-text3,#45688a)', fontSize: '.85rem' }}>
            {es ? 'No hay agentes para mostrar todavía.' : 'No agents to show yet.'}
          </div>
        ) : (
          <div style={{ overflowX: 'auto', border: '1px solid var(--c-border,#1a3550)', borderRadius: 12 }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 1080 }}>
              <thead>
                <tr style={{ background: 'var(--c-surface2,#112538)' }}>
                  {COLS.map((col) => (
                    <th key={col.key} style={{ ...th, textAlign: col.kind === 'num' || col.kind === 'money' ? 'right' : 'left' }}>
                      {es ? col.es : col.en}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {data.rows.map((r, i) => (
                  <tr key={r.userId} style={{ borderTop: '1px solid var(--c-border,#1a3550)', background: i % 2 ? 'var(--c-surface2,#112538)' : 'transparent' }}>
                    {COLS.map((col) => cell(col, r, false))}
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr style={{ borderTop: '2px solid var(--c-border,#1a3550)', background: 'var(--c-surface3,#162d44)' }}>
                  {COLS.map((col) => cell(col, { ...data.totals, name: '', role: '' }, true))}
                </tr>
              </tfoot>
            </table>
          </div>
        )
      )}
    </div>
  );
}
